import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Trash2, MessageSquarePlus, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CommentCard } from "./CommentCard";

interface TextSelection {
  start: number;
  end: number;
  text: string;
  originalText: string;
}

interface Comment {
  id: string;
  text: string;
  color: string;
  number: number;
  textSelections: TextSelection[];
  isGeneral: boolean;
  textChanged?: boolean;
}

interface UpdatedCommentsPanelProps {
  comments: Comment[];
  onAddComment: (text: string) => void;
  onDeleteComment: (commentId: string) => void;
  onEditComment: (commentId: string, newText: string) => void;
  onUpdateSelection?: (commentId: string, newStart: number, newEnd: number) => void;
  onAddAttachment?: (files: File[]) => void;
  selectedText: string;
  highlightedCommentId?: string | null;
  isComposingComment: boolean;
  setIsComposingComment: (isComposing: boolean) => void;
}

export const UpdatedCommentsPanel = ({
  comments,
  onAddComment,
  onDeleteComment,
  onEditComment,
  onUpdateSelection,
  onAddAttachment,
  selectedText,
  highlightedCommentId,
  isComposingComment,
  setIsComposingComment,
}: UpdatedCommentsPanelProps) => {
  const [newCommentText, setNewCommentText] = useState("");
  const [showAddGeneral, setShowAddGeneral] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [warningDialogComment, setWarningDialogComment] = useState<Comment | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [adjustStart, setAdjustStart] = useState(0);
  const [adjustEnd, setAdjustEnd] = useState(0);
  const newCommentTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus textarea when add comment form appears
  useEffect(() => {
    if (showAddGeneral && newCommentTextareaRef.current) {
      newCommentTextareaRef.current.focus();
    }
  }, [showAddGeneral]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0 && onAddAttachment) {
      onAddAttachment(files);
    }
  };

  const handleAddGeneralComment = () => {
    console.log('[UpdatedCommentsPanel] handleAddGeneralComment called', {
      hasText: !!newCommentText.trim(),
      textLength: newCommentText.length
    });
    if (newCommentText.trim()) {
      onAddComment(newCommentText);
      setNewCommentText("");
      setShowAddGeneral(false);
      console.log('[UpdatedCommentsPanel] Clearing isComposingComment flag');
      setIsComposingComment(false);
    }
  };

  const handleEditComment = (commentId: string, text: string) => {
    setEditingCommentId(commentId);
    setEditText(text);
  };

  const handleSaveEdit = () => {
    if (editingCommentId && editText.trim()) {
      onEditComment(editingCommentId, editText);
      setEditingCommentId(null);
      setEditText("");
    }
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditText("");
  };

  const handleShowWarningDialog = (comment: Comment) => {
    if (comment.textSelections.length > 0) {
      const sel = comment.textSelections[0];
      setAdjustStart(sel.start);
      setAdjustEnd(sel.end);
    }
    setWarningDialogComment(comment);
  };

  const handleUpdateSelection = () => {
    if (warningDialogComment && onUpdateSelection) {
      onUpdateSelection(warningDialogComment.id, adjustStart, adjustEnd);
      setWarningDialogComment(null);
    }
  };

  const colorBorderClasses = {
    blue: 'border-l-4 border-blue-500',
    amber: 'border-l-4 border-amber-500',
    orange: 'border-l-4 border-orange-500',
    purple: 'border-l-4 border-purple-500',
    green: 'border-l-4 border-green-500',
    pink: 'border-l-4 border-pink-500',
    teal: 'border-l-4 border-teal-500',
  };

  const colorBgClasses = {
    blue: 'bg-blue-500/5',
    amber: 'bg-amber-500/5',
    orange: 'bg-orange-500/5',
    purple: 'bg-purple-500/5',
    green: 'bg-green-500/5',
    pink: 'bg-pink-500/5',
    teal: 'bg-teal-500/5',
  };

  const numberBgClasses = {
    blue: 'bg-blue-500',
    amber: 'bg-amber-500',
    orange: 'bg-orange-500',
    purple: 'bg-purple-500',
    green: 'bg-green-500',
    pink: 'bg-pink-500',
    teal: 'bg-teal-500',
  };

  return (
    <>
      <div 
        className="flex flex-col h-full relative"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragging && (
          <div className="absolute inset-0 border-2 border-dashed border-primary bg-primary/5 pointer-events-none z-10 flex items-center justify-center">
            <div className="bg-background/90 px-4 py-2 rounded-md shadow-lg">
              <p className="text-sm font-medium">Drop files to add as attachments</p>
            </div>
          </div>
        )}
        <div className="p-3 border-b flex items-center justify-end">
          {!showAddGeneral && (
            <Button
              size="sm"
              variant="outline"
              onMouseDown={(e) => {
                // Prevent blur event from firing - this is key to preventing race condition
                e.preventDefault();
                console.log('[UpdatedCommentsPanel] Add button mouseDown - prevented blur');
              }}
              onClick={() => {
                console.log('[UpdatedCommentsPanel] Add button clicked', {
                  hasSelectedText: !!selectedText,
                  selectedText: selectedText.substring(0, 50) + (selectedText.length > 50 ? '...' : '')
                });
                setShowAddGeneral(true);
                // Lock selection if there's text selected
                if (selectedText) {
                  console.log('[UpdatedCommentsPanel] Locking selection - setting isComposingComment=true');
                  setIsComposingComment(true);
                } else {
                  console.log('[UpdatedCommentsPanel] No selection to lock');
                }
              }}
            >
              <MessageSquarePlus className="w-4 h-4 mr-1" />
              Add
            </Button>
          )}
        </div>

        {showAddGeneral && (
          <div className="p-4 border-b">
            <Card className="p-3 space-y-2">
              {selectedText && (
                <div className="mb-2 p-2 bg-blue-500/10 border-l-3 border-blue-500 rounded">
                  <div className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">
                    Selected text:
                  </div>
                  <div className="text-xs italic">
                    "{selectedText}"
                  </div>
                </div>
              )}
              <Textarea
                ref={newCommentTextareaRef}
                placeholder={selectedText ? "Add comment to selected text..." : "Add a general comment..."}
                value={newCommentText}
                onChange={(e) => {
                  console.log('[UpdatedCommentsPanel] Comment text changed', {
                    length: e.target.value.length,
                    wasEmpty: !newCommentText,
                    isComposingComment
                  });
                  setNewCommentText(e.target.value);
                  // Lock selection when user starts typing
                  if (e.target.value && !isComposingComment) {
                    console.log('[UpdatedCommentsPanel] User started typing - setting isComposingComment=true');
                    setIsComposingComment(true);
                  }
                }}
                className="min-h-[80px]"
              />
              <div className="flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    console.log('[UpdatedCommentsPanel] Cancel button clicked - clearing isComposingComment');
                    setShowAddGeneral(false);
                    setNewCommentText("");
                    setIsComposingComment(false);
                  }}
                >
                  Cancel
                </Button>
                <Button size="sm" onClick={handleAddGeneralComment}>
                  Add Comment
                </Button>
              </div>
            </Card>
          </div>
        )}

        <ScrollArea className="flex-1">
          <div className="space-y-3 p-4">
            {comments.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No comments yet. Add a comment to get started.
              </p>
            ) : (
              comments.map((comment) => {
                // If editing this comment, show edit form
                if (editingCommentId === comment.id) {
                  return (
                    <Card key={comment.id} className="p-3">
                      <div className="space-y-2">
                        {!comment.isGeneral && comment.textSelections.length > 0 && (
                          <div className="mb-2 text-xs">
                            <span className="font-semibold">[{comment.number}]</span>{' '}
                            <span className="italic">
                              "{comment.textSelections[0].text.split(' ').slice(0, 5).join(' ')}..."
                            </span>
                          </div>
                        )}
                        <Textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          className="min-h-[80px] text-sm"
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={handleSaveEdit}>
                            Save
                          </Button>
                          <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                }
                
                // Otherwise show comment card with edit/delete buttons
                return (
                  <div key={comment.id} className="relative">
                    {comment.textChanged && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute -top-2 -right-2 h-6 w-6 p-0 bg-yellow-500 hover:bg-yellow-600 text-white rounded-full z-10"
                        onClick={() => handleShowWarningDialog(comment)}
                      >
                        <AlertCircle className="h-4 w-4" />
                      </Button>
                    )}
                    <CommentCard
                      comment={comment}
                      isHighlighted={highlightedCommentId === comment.id}
                      readOnly={false}
                      onEdit={handleEditComment}
                      onDelete={onDeleteComment}
                    />
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Warning Dialog for changed text */}
      <Dialog open={!!warningDialogComment} onOpenChange={(open) => !open && setWarningDialogComment(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Selection Text Changed</DialogTitle>
            <DialogDescription>
              The text this comment refers to has been modified. Review the changes below.
            </DialogDescription>
          </DialogHeader>
          
          {warningDialogComment && warningDialogComment.textSelections.length > 0 && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Original Selection:</label>
                <div className="mt-1 p-2 bg-muted rounded text-sm">
                  "{warningDialogComment.textSelections[0].originalText}"
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">Current Selection:</label>
                <div className="mt-1 p-2 bg-muted rounded text-sm">
                  "{warningDialogComment.textSelections[0].text}"
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-sm font-medium">Start Position:</label>
                  <input
                    type="number"
                    value={adjustStart}
                    onChange={(e) => setAdjustStart(parseInt(e.target.value))}
                    className="mt-1 w-full px-2 py-1 border rounded text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">End Position:</label>
                  <input
                    type="number"
                    value={adjustEnd}
                    onChange={(e) => setAdjustEnd(parseInt(e.target.value))}
                    className="mt-1 w-full px-2 py-1 border rounded text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              if (warningDialogComment) {
                onDeleteComment(warningDialogComment.id);
                setWarningDialogComment(null);
              }
            }}>
              Dismiss Comment
            </Button>
            <Button onClick={handleUpdateSelection}>
              Update Selection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
